import React from "react";
import { Card, Tag, Typography, Timeline, Image, Empty, Tooltip, Alert } from "antd";
import {
    SafetyOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    PictureOutlined,
    KeyOutlined,
    IdcardOutlined,
    BarcodeOutlined,
    AppstoreOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useQuery } from "@apollo/client";
import { getKeyAuditResultsQuery } from "../../util/query/keyRegister.query";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const S3_BASE_URL = "https://wfcrevampdoc.s3.us-east-1.amazonaws.com/";

const KEY_TYPE_CONFIG = {
    metal:      { label: "Metal",       icon: <KeyOutlined />,       color: "#8c8c8c" },
    fob:        { label: "Fob",         icon: <SafetyOutlined />,    color: "#1677ff" },
    card:       { label: "Card",        icon: <IdcardOutlined />,    color: "#722ed1" },
    accessCode: { label: "Access Code", icon: <BarcodeOutlined />,   color: "#13c2c2" },
};

const AUDIT_STATUS_CONFIG = {
    Scheduled: { color: "#08979c", bg: "#e6fffb", border: "#87e8de", tagColor: "processing" },
    Completed: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f", tagColor: "success" },
    Overdue:   { color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7", tagColor: "error" },
    Pending:   { color: "#fa8c16", bg: "#fff7e6", border: "#ffd591", tagColor: "warning" },
};

const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === "string") return S3_BASE_URL + image;
    if (image.url) return image.url;
    return null;
};

export const KeyAuditTimeline = ({ keyId, keyData }) => {

    const { data, loading } = useQuery(getKeyAuditResultsQuery, {
        variables: { keyId },
        skip: !keyId,
        fetchPolicy: "network-only",
    });

    const auditResults = (data?.getKeyAuditResult || [])
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    
    const status = keyData?.auditStatus;
    const statusConfig = AUDIT_STATUS_CONFIG[status] || AUDIT_STATUS_CONFIG.Pending;
    const nextDate = keyData?.auditNextDate ? dayjs(keyData.auditNextDate) : null;
    const lastDate = keyData?.auditLastDate ? dayjs(keyData.auditLastDate) : null;
    const isOverdue = nextDate && nextDate.isBefore(dayjs());

    const timelineItems = auditResults.map((result, index) => {
        const time = dayjs(result.createdAt);
        const images = (result.images || []).filter(Boolean);
        const isFirst = index === 0;

        return {
            key: result._id,
            color: isFirst ? "#52c41a" : "#1677ff",
            dot: (
                <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    background: isFirst ? "#f6ffed" : "#e6f4ff",
                    border: `2px solid ${isFirst ? "#52c41a" : "#1677ff"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, color: isFirst ? "#52c41a" : "#1677ff",
                }}>
                    <CheckCircleOutlined />
                </div>
            ),
            children: (
                <div style={{
                    background: isFirst ? "#f6ffed" : "#fff",
                    border: `1px solid ${isFirst ? "#b7eb8f" : "#f0f0f0"}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    marginBottom: 4,
                    marginLeft: 8,
                }}>
                    {/* Header row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {isFirst && (
                                <Tag color="success" style={{ margin: 0, borderRadius: 4, fontWeight: 500 }}>
                                    Most Recent
                                </Tag>
                            )}
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                {result.auditFor === "keygroup"
                                    ? <AppstoreOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                                    : <KeyOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                                }
                                <Text type="secondary" style={{ fontSize: 13, textTransform: "capitalize" }}>
                                    {result.auditFor}
                                </Text>
                            </div>
                            {result.isFullyConfirmed != null && (
                                result.isFullyConfirmed
                                    ? <Tag color="success" style={{ margin: 0, borderRadius: 4 }}>
                                        <CheckCircleFilled style={{ marginRight: 4 }} />Fully Confirmed
                                      </Tag>
                                    : <Tag color="error" style={{ margin: 0, borderRadius: 4 }}>
                                        <CloseCircleFilled style={{ marginRight: 4 }} />Incomplete
                                      </Tag>
                            )}
                        </div>
                        <Tooltip title={time.format("DD MMM YYYY, hh:mm A")}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <ClockCircleOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {time.format("DD MMM YYYY")}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    ({time.fromNow()})
                                </Text>
                            </div>
                        </Tooltip>
                    </div>

                    {/* Key confirmations summary */}
                    {result.keyConfirmations?.length > 0 && (
                        <div style={{
                            marginTop: 10,
                            background: "#fafafa",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: "10px 12px",
                        }}>
                            <Text strong style={{ fontSize: 12, display: "block", marginBottom: 8, color: "#595959" }}>
                                Audit Summary
                            </Text>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {result.keyConfirmations.map(kc => {
                                    const cfg = KEY_TYPE_CONFIG[kc.keyType] || {};
                                    return (
                                        <div key={kc.keyType}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <span style={{ color: cfg.color, fontSize: 13 }}>{cfg.icon}</span>
                                                    <Text style={{ fontSize: 12 }}>
                                                        {cfg.label || kc.keyType} × <strong>{kc.expectedCount}</strong>
                                                    </Text>
                                                </div>
                                                {kc.confirmed
                                                    ? <Tag color="success" style={{ borderRadius: 4, fontSize: 11 }}>Confirmed</Tag>
                                                    : <Tag color="error"   style={{ borderRadius: 4, fontSize: 11 }}>Not Confirmed</Tag>
                                                }
                                            </div>
                                            {!kc.confirmed && kc.reason && (
                                                <Text type="secondary" style={{ fontSize: 11, paddingLeft: 20, display: "block", marginTop: 2 }}>
                                                    Reason: {kc.reason}
                                                </Text>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Overall notes */}
                    {result.overallNotes && (
                        <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <FileTextOutlined style={{ color: "#8c8c8c", fontSize: 12, marginTop: 2 }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>{result.overallNotes}</Text>
                        </div>
                    )}

                    {/* Photos */}
                    {images.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                                <PictureOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {images.length} photo{images.length > 1 ? "s" : ""}
                                </Text>
                            </div>
                            <Image.PreviewGroup>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {images.map((img, i) => {
                                        const src = (img);
                                        if (!src) return null;
                                        return (
                                            <Image
                                                key={i}
                                                width={64}
                                                height={64}
                                                src={src}
                                                style={{ borderRadius: 6, objectFit: "cover", border: "1px solid #f0f0f0" }}
                                            />
                                        );
                                    })}
                                </div>
                            </Image.PreviewGroup>
                        </div>
                    )}
                </div>
            ),
        };
    });

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Audit Status Header */}
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div style={{
                        width: 52, height: 52, borderRadius: 12,
                        background: statusConfig.bg,
                        border: `1.5px solid ${statusConfig.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 22, color: statusConfig.color, flexShrink: 0,
                    }}>
                        <SafetyOutlined />
                    </div>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <Title level={5} style={{ margin: 0 }}>Audit Status</Title>
                            {status ? (
                                <Tag color={statusConfig.tagColor} style={{ borderRadius: 6, fontWeight: 500 }}>
                                    {status}
                                </Tag>
                            ) : (
                                <Text type="secondary" style={{ fontSize: 13 }}>—</Text>
                            )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {auditResults.length} audit{auditResults.length !== 1 ? "s" : ""} recorded
                        </Text>
                    </div>
                </div>

                {/* Last / Next date pills */}
                <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        background: "#fafafa", border: "1px solid #f0f0f0",
                        borderRadius: 8, padding: "8px 14px",
                    }}>
                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Last Audit</Text>
                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                {lastDate ? lastDate.format("DD MMM YYYY") : "—"}
                            </Text>
                        </div>
                    </div>

                    <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        background: isOverdue ? "#fff2f0" : "#fafafa",
                        border: `1px solid ${isOverdue ? "#ffccc7" : "#f0f0f0"}`,
                        borderRadius: 8, padding: "8px 14px",
                    }}>
                        <CalendarOutlined style={{ color: isOverdue ? "#ff4d4f" : "#1677ff", fontSize: 14 }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Next Expected Audit</Text>
                            <Tooltip title={nextDate ? nextDate.fromNow() : undefined}>
                                <Text style={{ fontSize: 13, fontWeight: 500, color: isOverdue ? "#ff4d4f" : undefined }}>
                                    {nextDate ? nextDate.format("DD MMM YYYY") : "—"}
                                    {isOverdue && " (Overdue)"}
                                </Text>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Overdue alert */}
            {isOverdue && (
                <Alert
                    type="error"
                    showIcon
                    message="Audit Overdue"
                    description={`This key's audit was due ${nextDate.fromNow()}. Please schedule an audit as soon as possible.`}
                    style={{ borderRadius: 8 }}
                />
            )}

            {/* Audit History Timeline */}
            <Card
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                        <ClockCircleOutlined />
                        <span>Audit History</span>
                        {auditResults.length > 0 && (
                            <Tag style={{ marginLeft: 4, borderRadius: 4 }}>{auditResults.length}</Tag>
                        )}
                    </div>
                }
                style={{ borderRadius: 12 }}
                bodyStyle={{ padding: "16px 20px" }}
                headStyle={{ padding: "0 20px", minHeight: 44 }}
                loading={loading}
            >
                {!loading && auditResults.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No audit results recorded"
                        style={{ margin: "20px 0" }}
                    />
                ) : (
                    <Timeline
                        mode="left"
                        items={timelineItems}
                        style={{ marginTop: 8 }}
                    />
                )}
            </Card>
        </div>
    );
};
